import type { SortDirection } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { OrganizationView } from '@vizit/api';
import { Cell, Row, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { useVirtualizedLoadMore } from '@vizit/hooks';
import { Building06Icon } from '@vizit/icons';
import { LibraryEmptyState } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { Table, TableBody } from 'react-aria-components';
import type { OrganizationFormSchema } from '../organization-dialog';
import { OrganizationRow } from '../organization-row';
import { organizationTableWrapperClasses, tableClasses, tableContainerClasses } from './organization-table.classes';
import { OrganizationTableHeader } from './organization-table.header';
import { OrganizationTableSkeleton } from './organization-table.loading';

type OrganizationData = OrganizationView & {
  product_categories: { id: string; category_name: string }[];
  created_by: string;
};

interface OrganizationTableProps {
  /**
   * ARIA label for the table
   *
   * @optional
   * @type string
   * @default "Organization table"
   */
  'aria-label'?: string;
  /**
   * Additional CSS classes to apply to the table
   *
   * @optional
   * @type string
   */
  className?: string;
  /**
   * Whether the next page is being fetched
   */
  isFetchingNextPage?: boolean;
  /**
   * Whether the table is in a loading state
   *
   * @optional
   * @type boolean
   * @default false
   */
  isLoading?: boolean;
  /**
   * Whether the ICP product categories are still loading
   *
   * @optional
   * @type boolean
   * @default false
   */
  isCategoriesLoading?: boolean;
  /**
   * Whether the created by (users) data is still loading
   *
   * @optional
   * @type boolean
   * @default false
   */
  isCreatedByLoading?: boolean;
  /**
   * Array of ICP product categories to display in the table
   *
   * @required
   * @type Record<string, string>
   */
  icpProductCategories: Record<string, string>;
  /**
   * Available retailer regions to show in the edit dialog (id → display_name)
   *
   * @optional
   * @type Record<string, string>
   */
  retailerRegions?: Record<string, string>;
  /**
   * Array of organization data to display in the table
   *
   * @required
   * @type OrganizationData[]
   */
  organizations: OrganizationData[];
  /**
   * Total known count of rows
   */
  totalCount: number;
  /**
   * The field currently being sorted
   * @optional
   */
  sortBy?: string;
  /**
   * The current sort direction
   * @optional
   */
  sortDirection?: SortDirection | false;
  /**
   * Callback when a sortable column header is clicked
   * @optional
   */
  onSort?: (field: string) => void;
  /**
   * Callback fired when the Edit button is clicked
   *
   * @optional
   * @type (data: OrganizationFormSchema, defaultValues: OrganizationFormSchema) => void
   */
  onEdit?: (
    data: OrganizationFormSchema,
    defaultValues: OrganizationFormSchema,
    organizationId: string,
  ) => Promise<void> | void;
  /**
   * Called when the list needs to load more (bottom reached)
   */
  onLoadMore?: () => void;
  /**
   * Callback fired when a row is clicked
   *
   * @optional
   * @type (organizationId: string) => void
   */
  onRowClick?: (organizationId: string) => void;
  /**
   * Callback fired when the View button is clicked
   *
   * @optional
   * @type (organizationId: string) => void
   */
  onView?: (organizationId: string, organizationName: string, portfolioId: string) => void;
}

const ESTIMATED_ROW_HEIGHT_PX = 128;

/**
 * Virtualized organization table component with server-side sorting support.
 * Displays organization data with infinite scroll loading and sortable column headers.
 *
 * @param props - Table configuration including organizations data, sort state, and callbacks
 */
function OrganizationTable(props: OrganizationTableProps) {
  const {
    'aria-label': ariaLabel = 'Organization table',
    className,
    isFetchingNextPage,
    isLoading = false,
    isCategoriesLoading = false,
    isCreatedByLoading = false,
    organizations,
    totalCount,
    sortBy,
    sortDirection,
    onSort,
    icpProductCategories,
    retailerRegions,
    onEdit,
    onLoadMore,
    onRowClick,
    onView,
    ...other
  } = props;

  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: totalCount,
    getScrollElement: () => tableContainerRef.current,
    overscan: 6,
    estimateSize: () => ESTIMATED_ROW_HEIGHT_PX,
    measureElement: (element) => element?.getBoundingClientRect?.().height ?? ESTIMATED_ROW_HEIGHT_PX,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const lastVirtualIndex = virtualItems.length ? virtualItems[virtualItems.length - 1].index : -1;

  useVirtualizedLoadMore({
    dataLength: organizations.length,
    lastVirtualIndex,
    isFetchingNextPage,
    onLoadMore,
  });

  if (isLoading) {
    return <OrganizationTableSkeleton />;
  }

  if (organizations.length === 0) {
    return (
      <LibraryEmptyState
        heading="You don't have any organizations yet"
        description="Start by creating your first organization!"
        icon={Building06Icon}
      />
    );
  }

  return (
    <div className={organizationTableWrapperClasses()}>
      <Text className="text-left w-fit" size={TextSize.Sm} color={TextColor.Tertiary} weight={TextWeight.Medium}>
        {totalCount > 1 ? `${totalCount} organizations` : `${totalCount} organization`}
      </Text>
      <div ref={tableContainerRef} className={cx(tableContainerClasses(), className)}>
        <Table className={tableClasses()} aria-label={ariaLabel} {...other}>
          <OrganizationTableHeader sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} />
          <TableBody>
            {virtualItems.length > 0 && (
              <>
                {(() => {
                  const first = virtualItems[0];
                  const beforeHeight = first.start;
                  return beforeHeight > 0 ? (
                    <Row aria-hidden={true}>
                      <Cell colSpan={6}>
                        <div style={{ height: beforeHeight }} />
                      </Cell>
                    </Row>
                  ) : null;
                })()}

                {virtualItems.map((virtualRow) => {
                  const isDataRow = virtualRow.index < organizations.length;
                  const organization = organizations[virtualRow.index];
                  return isDataRow ? (
                    <OrganizationRow
                      key={organization.id}
                      data-index={virtualRow.index}
                      ref={(element: HTMLTableRowElement) => rowVirtualizer.measureElement(element)}
                      organizationId={organization.id || ''}
                      name={organization.name || ''}
                      accountType={organization.account_type || ''}
                      productCategories={organization.product_categories || []}
                      icpProductCategories={icpProductCategories}
                      retailerRegions={retailerRegions}
                      isCategoriesLoading={isCategoriesLoading}
                      isCreatedByLoading={isCreatedByLoading}
                      createdBy={organization.created_by || ''}
                      createdAt={organization.created_at}
                      portfolioId={organization.default_portfolio_id || ''}
                      onRowClick={onRowClick}
                      onView={onView}
                      onEdit={onEdit}
                    />
                  ) : (
                    <OrganizationRow
                      data-index={virtualRow.index}
                      ref={(element: HTMLTableRowElement) => rowVirtualizer.measureElement(element)}
                      key={`skeleton-row-${virtualRow.index}`}
                      organizationId={`skeleton-row-${virtualRow.index}`}
                      name=""
                      accountType=""
                      productCategories={[]}
                      icpProductCategories={icpProductCategories}
                      createdBy=""
                      portfolioId=""
                      isLoading={true}
                    />
                  );
                })}

                {(() => {
                  const last = virtualItems[virtualItems.length - 1];
                  const afterHeight = rowVirtualizer.getTotalSize() - (last.start + last.size);
                  return afterHeight > 0 ? (
                    <Row aria-hidden={true}>
                      <Cell colSpan={6}>
                        <div style={{ height: afterHeight }} />
                      </Cell>
                    </Row>
                  ) : null;
                })()}
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export { OrganizationTable };
export type { OrganizationTableProps, OrganizationData };
